import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localeFor } from '../utils/timeFormat';
import './SupportWidget.css';

const MAX_LENGTH = 3000;
const UNREAD_POLL_MS = 60000;
const THREAD_POLL_MS = 15000;

// Открыть окно помощи из любого места приложения, например из уведомления:
// openSupport(ticketId) сразу показывает нужную переписку
export const SUPPORT_OPEN_EVENT = 'support:open';
export const openSupport = (ticketId) =>
  window.dispatchEvent(new CustomEvent(SUPPORT_OPEN_EVENT, { detail: { ticketId } }));

// Кнопка помощи в правом нижнем углу и окно переписки с поддержкой. Автор видит
// свои обращения, поддержка (analytics_access) — все и отвечает в них же; ответ приходит автору уведомлением
const SupportWidget = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [onlyUnanswered, setOnlyUnanswered] = useState(true);
  const messagesEndRef = useRef(null);

  // Отвечает на обращения и видит чужие только поддержка — users.analytics_access
  const isSupport = !!user?.analytics_access;
  const enabled = !!user && !user.deactivated;

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get('/api/support_tickets/unread_count');
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch support unread count:', err);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, UNREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [enabled, fetchUnreadCount]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/support_tickets');
      setTickets(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || t('support.loadError'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadTicket = useCallback(async (id, { silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const response = await axios.get(`/api/support_tickets/${id}`);
      setTicket(response.data);
      fetchUnreadCount();
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || t('support.loadError'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fetchUnreadCount, t]);

  const showList = useCallback(async () => {
    setView('list');
    setTicket(null);
    setDraft('');
    const list = await loadTickets();
    // Пользователю без обращений список пуст и бесполезен — сразу форма вопроса
    if (list && list.length === 0 && !isSupport) setView('new');
  }, [loadTickets, isSupport]);

  const showTicket = useCallback((id) => {
    setView('ticket');
    setDraft('');
    setTicket(null);
    loadTicket(id);
  }, [loadTicket]);

  const open = () => {
    setIsOpen(true);
    showList();
  };

  const close = () => {
    setIsOpen(false);
    setError('');
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const handleOpen = (event) => {
      setIsOpen(true);
      const ticketId = event.detail?.ticketId;
      if (ticketId) showTicket(ticketId);
      else showList();
    };

    window.addEventListener(SUPPORT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, handleOpen);
  }, [enabled, showList, showTicket]);

  // Открытая переписка подтягивает новые сообщения, пока окно не закрыли
  const ticketId = ticket?.id;
  useEffect(() => {
    if (!isOpen || view !== 'ticket' || !ticketId) return undefined;

    const interval = setInterval(() => loadTicket(ticketId, { silent: true }), THREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [isOpen, view, ticketId, loadTicket]);

  const messagesCount = ticket?.messages?.length;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messagesCount]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  const submitQuestion = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;

    setSending(true);
    setError('');
    try {
      const response = await axios.post('/api/support_tickets', { body });
      showTicket(response.data.id);
    } catch (err) {
      setError(err.response?.data?.error || t('support.sendError'));
    } finally {
      setSending(false);
    }
  };

  const submitReply = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || !ticket) return;

    setSending(true);
    setError('');
    try {
      await axios.post(`/api/support_tickets/${ticket.id}/messages`, { body });
      setDraft('');
      await loadTicket(ticket.id, { silent: true });
    } catch (err) {
      setError(err.response?.data?.error || t('support.sendError'));
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!ticket || !window.confirm(t('support.closeConfirm'))) return;

    try {
      await axios.post(`/api/support_tickets/${ticket.id}/close`);
      await loadTicket(ticket.id, { silent: true });
    } catch (err) {
      setError(err.response?.data?.error || t('support.sendError'));
    }
  };

  // Enter в поле — перенос строки, Ctrl/Cmd+Enter — отправка
  const submitOnShortcut = (event) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.currentTarget.form?.requestSubmit();
    }
  };

  const formatDate = (value) =>
    new Date(value).toLocaleString(localeFor(language), {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const authorName = (author) =>
    [author.first_name, author.last_name].filter(Boolean).join(' ') || author.nickname;

  if (!enabled) return null;

  const visibleTickets = isSupport && onlyUnanswered
    ? tickets.filter((item) => item.status === 'open')
    : tickets;

  const renderComposer = (onSubmit, placeholder, submitLabel) => (
    <form className="support-composer" onSubmit={onSubmit}>
      <textarea
        className="support-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={submitOnShortcut}
        placeholder={placeholder}
        maxLength={MAX_LENGTH}
        rows={view === 'new' ? 6 : 3}
        autoFocus
      />
      <div className="support-composer-footer">
        <span className="support-hint">{t('support.sendHint')}</span>
        <button type="submit" className="support-send" disabled={sending || !draft.trim()}>
          {sending ? t('common.submitting') : submitLabel}
        </button>
      </div>
    </form>
  );

  const renderList = () => (
    <>
      <div className="support-toolbar">
        {isSupport ? (
          <div className="support-filter">
            <button
              type="button"
              className={onlyUnanswered ? 'active' : ''}
              onClick={() => setOnlyUnanswered(true)}
            >
              {t('support.filterOpen')}
            </button>
            <button
              type="button"
              className={!onlyUnanswered ? 'active' : ''}
              onClick={() => setOnlyUnanswered(false)}
            >
              {t('support.filterAll')}
            </button>
          </div>
        ) : (
          <button type="button" className="support-send" onClick={() => { setDraft(''); setView('new'); }}>
            {t('support.newQuestion')}
          </button>
        )}
      </div>

      {loading && <div className="support-empty">{t('common.loading')}</div>}
      {!loading && visibleTickets.length === 0 && (
        <div className="support-empty">{t(isSupport ? 'support.noOpenTickets' : 'support.noTickets')}</div>
      )}

      <ul className="support-tickets">
        {visibleTickets.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={`support-ticket ${item.unread ? 'unread' : ''}`}
              onClick={() => showTicket(item.id)}
            >
              <span className="support-ticket-head">
                {isSupport && item.user.id !== user.id && (
                  <span className="support-ticket-author">{authorName(item.user)} @{item.user.nickname}</span>
                )}
                <span className={`support-status support-status-${item.status}`}>
                  {t(`support.status.${item.status}`)}
                </span>
                <span className="support-ticket-date">{formatDate(item.last_message_at)}</span>
              </span>
              <span className="support-ticket-text">{item.last_message}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  const renderTicket = () => {
    if (!ticket) {
      return loading ? <div className="support-empty">{t('common.loading')}</div> : null;
    }

    return (
      <>
        <div className="support-toolbar">
          {isSupport && ticket.user.id !== user.id ? (
            <span className="support-ticket-author">{authorName(ticket.user)} @{ticket.user.nickname}</span>
          ) : (
            <span className={`support-status support-status-${ticket.status}`}>
              {t(`support.status.${ticket.status}`)}
            </span>
          )}
          {ticket.status !== 'closed' && (
            <button type="button" className="support-link" onClick={closeTicket}>
              {t('support.closeTicket')}
            </button>
          )}
        </div>

        <div className="support-messages">
          {ticket.messages.map((message) => (
            <div key={message.id} className={`support-message ${message.from_support ? 'from-support' : 'from-user'}`}>
              <div className="support-message-meta">
                {message.from_support ? t('support.supportName') : authorName(message.user)}
                {' · '}
                {formatDate(message.created_at)}
              </div>
              <div className="support-message-body">{message.body}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {ticket.status === 'closed' && <div className="support-closed-note">{t('support.closedNote')}</div>}
        {renderComposer(submitReply, t('support.replyPlaceholder'), t('support.send'))}
      </>
    );
  };

  const title = view === 'new' ? t('support.newQuestion') : t('support.title');

  return (
    <>
      <button
        type="button"
        className="support-fab"
        onClick={open}
        aria-label={t('support.open')}
        title={t('support.open')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"
          />
        </svg>
        {unreadCount > 0 && <span className="support-fab-badge">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="support-overlay" onClick={close}>
          <div
            className="support-modal"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="support-header">
              {view !== 'list' && !(view === 'new' && !isSupport && tickets.length === 0) && (
                <button type="button" className="support-icon-button" onClick={showList} aria-label={t('common.back')}>
                  ←
                </button>
              )}
              <h3 className="support-title">{title}</h3>
              <button type="button" className="support-icon-button" onClick={close} aria-label={t('common.close')}>
                ×
              </button>
            </div>

            <div className="support-body">
              {error && <div className="support-error">{error}</div>}
              {view === 'list' && renderList()}
              {view === 'new' && (
                <>
                  <p className="support-intro">{t('support.intro')}</p>
                  {renderComposer(submitQuestion, t('support.questionPlaceholder'), t('support.send'))}
                </>
              )}
              {view === 'ticket' && renderTicket()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportWidget;
